import { Form } from "@unform/web";
import { XCircle } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../../..";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import { yesOrNoOptions } from "~/functions/selectPopoverOptions";

export const InputContext = createContext({});

export default function CancelRecurrence({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);

  const [pageData, setPageData] = useState({ loaded: false });
  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState(false);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit() {
    if (cancelConfirm && !cancelReason.trim()) {
      toast("Cancel reason is required.", { type: "error", autoClose: 3000 });
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/recurrence/${selected[0].id}`, {
        data: cancelConfirm ? { cancel_reason: cancelReason } : {},
      });
      toast("Recurrence canceled!", { autoClose: 1000 });
      setSuccessfullyUpdated(true);
      handleOpened(null);
    } catch (err) {
      toast(err?.response?.data?.error || "Unexpected error.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!selected?.[0]?.id) return;
    async function loadRecurrence() {
      try {
        const { data } = await api.get(`/recurrence/${selected[0].id}`);
        setPageData({ ...data, loaded: true });
      } catch (err) {
        console.error("Failed to load recurrence", err);
      }
    }
    loadRecurrence();
  }, [selected?.[0]?.id]);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <XCircle size={16} /> Cancel
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={
                          "Cancel Recurrence - " +
                          pageData?.issuer?.student?.name +
                          " " +
                          pageData?.issuer?.student?.last_name
                        }
                        registry={{}}
                        InputContext={InputContext}
                        saveText="Cancel Recurrence"
                        saveColor="bg-red-600"
                        loading={loading}
                      />

                      <InputLineGroup
                        title="CANCEL RECURRENCE"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Confirmation">
                          <SelectPopover
                            name="cancel_confirm"
                            title="Do you want to cancel this recurrence?"
                            grow
                            options={yesOrNoOptions}
                            defaultValue={yesOrNoOptions.find(
                              (o) => o.value === false
                            )}
                            onChange={(el) => {
                              setCancelConfirm(el.value);
                              if (!el.value) setCancelReason("");
                              setSuccessfullyUpdated(false);
                            }}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        {cancelConfirm && (
                          <InputLine title="Cancel Reason">
                            <Textarea
                              name="cancel_reason"
                              title="Cancel Reason"
                              grow
                              required
                              rows={4}
                              placeholder="Recurrence canceled will cancel all pending receivables and other configurations."
                              onChange={(el) => {
                                setCancelReason(el.target.value);
                                setSuccessfullyUpdated(false);
                              }}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        )}
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
